import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import { PROFILE_CONTINUITY_CHANGE_EVENT, getProfileContinuityStatus } from "../utils/profileContinuity";
import { syncProfileContinuityFromRest } from "../utils/profileContinuityRest";
import { extractDreamText, sanitizeDreamHtml } from "../utils/safeDreamHtml";

const emptyPokoikStats = { all: 0, month: 0, week: 0 };
const POKOIK_REST_BUILD = "pokoik-rest-2026-05-04-e2e-only";
const DREAM_ICONS = ["🌕", "🌙", "🛏️", "🌌", "✨", "🌠", "💤", "🌀"];

const pokoikLinks = {
  add: "#/dodaj-sen",
  all: "#/moje-sny",
  editLast: "#/moje-sny",
  home: "#top",
};

const getPokoikCopy = (lang) => ({
  savedDream: lang === "en" ? "Dream saved" : "Sen zapisany",
  untitledDream: lang === "en" ? "Untitled dream" : "Sen bez tytułu",
  e2eLockedExcerpt: lang === "en" ? "🔒 This dream is encrypted (E2E)." : "🔒 Ten sen jest zaszyfrowany (E2E).",
  savedInCodex: lang === "en" ? "Dream saved in the Dream Codex." : "Sen zapisany w Księdze Snów.",
  vaultChecking: lang === "en" ? "checking…" : "sprawdzam…",
  vaultUnlocked: lang === "en" ? "unlocked" : "odblokowany",
  vaultLocked: lang === "en" ? "locked" : "zablokowany",
  vaultUnavailable: lang === "en" ? "unavailable" : "niedostępny",
  vaultMissing: lang === "en" ? "no vault" : "brak sejfu",
  gateEyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  gateTitlePre: lang === "en" ? "Welcome to" : "Witaj w",
  gateSubtitle: lang === "en"
    ? "Save dreams, discover symbols and run AI analyses. Create an account or sign in to enter your Room."
    : "Zapisuj sny, odkrywaj symbole i uruchamiaj analizy AI. Załóż konto lub zaloguj się, aby wejść do swojego Pokoiku.",
  signIn: lang === "en" ? "🔑 Sign in" : "🔑 Zaloguj się",
  register: lang === "en" ? "✨ Create account" : "✨ Załóż konto",
  home: lang === "en" ? "🏠 Home" : "🏠 Strona główna",
  loadingTitle: lang === "en" ? "Opening the Dream Codex…" : "Otwieram Księgę Snów…",
  loadingSub: lang === "en" ? "Checking the session and preparing your Room." : "Sprawdzam sesję i przygotowuję Twój Pokoik.",
  fetchingDreams: lang === "en" ? "Fetching dreams…" : "Pobieram sny…",
  loadingPatience: lang === "en" ? "one moment" : "chwila cierpliwości",
  openingPrivateSpace: lang === "en" ? "Morpheus opens your private space." : "Morfeusz otwiera Twoją prywatną przestrzeń.",
  allDreams: lang === "en" ? "All your dreams" : "Twoje sny razem",
  thisMonth: lang === "en" ? "This month" : "W tym miesiącu",
  thisWeek: lang === "en" ? "This week" : "W tym tygodniu",
  fetchError: lang === "en" ? "Could not fetch dreams." : "Nie udało się pobrać snów.",
  welcome: lang === "en" ? "Welcome to your room," : "Witaj w swoim pokoiku,",
  dreamer: lang === "en" ? "Dreamer" : "Śniący",
  subtitle: lang === "en"
    ? "This is your private space in Morfeusz Dreams — save dreams, return to them and discover meanings at your own pace."
    : "To Twoja prywatna przestrzeń w Morfeusz Dreams — zapisuj sny, wracaj do nich i odkrywaj znaczenia w swoim tempie.",
  noDreamTitle: lang === "en" ? "No dreams yet — begin with the first entry" : "Brak snów – zacznij od pierwszego wpisu",
  noDreams: lang === "en" ? "You do not have any dreams yet." : "Nie masz jeszcze żadnych snów.",
  vaultE2E: lang === "en" ? "E2E vault:" : "Sejf E2E:",
  dreamTrace: lang === "en" ? "Dream trace:" : "Ślad snów:",
  addDream: lang === "en" ? "Add new dream" : "Dodaj nowy sen",
  allDreamsButton: lang === "en" ? "See all dreams" : "Zobacz wszystkie sny",
  editLast: lang === "en" ? "Edit last" : "Edytuj ostatni",
  homeButton: lang === "en" ? "Home" : "Strona główna",
  profile: {
    active: lang === "en" ? "active" : "aktywny",
    enabledWaiting: lang === "en" ? "enabled — waiting for first trace" : "włączony — czeka na pierwszy ślad",
    enabledText: lang === "en"
      ? "Morpheus may recognize recurring symbols and include them in future analyses."
      : "Morfeusz może rozpoznawać powracające symbole i uwzględniać je w kolejnych analizach.",
    buildingUnused: lang === "en" ? "building — not used in analyses" : "budowany — nieużywany w analizach",
    buildingText: lang === "en"
      ? "The dream trace may gather encrypted traces of motifs, but it is not yet used as context for 4E."
      : "Ślad snów może zbierać zaszyfrowane ślady motywów, ale nie jest jeszcze używany jako kontekst dla 4E.",
    inactive: lang === "en" ? "not active yet" : "jeszcze nieaktywny",
    inactiveText: lang === "en"
      ? "You can configure the dream trace in its settings. The Room only shows its current status."
      : "Ślad snów ustawisz w jego ustawieniach. Pokoik pokazuje tylko jego aktualny status.",
    button: lang === "en" ? "Set dream trace" : "Ustaw Ślad snów",
  },
});

const toDate = (dream) => {
  const raw = dream?.date || dream?.meta?.date || dream?.createdAt;
  const parsed = raw ? new Date(raw) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const isSameWeek = (date, now) => {
  if (!date) return false;
  const start = new Date(now);
  const day = start.getDay() || 7;
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day + 1);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return date >= start && date < end;
};

const getDreamSortTime = (dream) => {
  const raw = dream?.createdAt || dream?.updatedAt || [dream?.date, dream?.time].filter(Boolean).join("T") || dream?.id || "";
  const parsed = raw ? new Date(raw) : null;
  if (parsed && !Number.isNaN(parsed.getTime())) return parsed.getTime();
  const numericId = Number(dream?.wpId || dream?.id || 0);
  return Number.isFinite(numericId) ? numericId : 0;
};

const getDreamIcon = (dream) => {
  const key = String(dream?.id || dream?.wpId || dream?.title || "");
  let sum = 0;
  for (let index = 0; index < key.length; index += 1) sum += key.charCodeAt(index);
  return DREAM_ICONS[sum % DREAM_ICONS.length] || "🌙";
};

const formatDreamDate = (dream, copy) => {
  const parts = [];
  if (dream?.date || dream?.meta?.date) parts.push(dream.date || dream.meta.date);
  if (dream?.time || dream?.meta?.time) parts.push(dream.time || dream.meta.time);
  return parts.length ? parts.join(" · ") : copy.savedDream;
};

const isVaultUnlocked = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.MDVaultSession?.getState?.()?.unlocked || window.MDVaultSession?.isUnlocked?.());
  } catch (_) {
    return false;
  }
};

const getVaultStatusLabel = (copy) => {
  if (typeof window === "undefined") return copy.vaultChecking;
  try {
    const state = window.MDVaultSession?.getState?.() || {};
    if (state.unlocked || window.MDVaultSession?.isUnlocked?.()) return copy.vaultUnlocked;
    if (state.source === "vault-bridge-v1") return copy.vaultChecking;
    if (state.configured) return copy.vaultLocked;
    if (state.available === false) return copy.vaultUnavailable;
    return copy.vaultMissing;
  } catch (_) {
    return copy.vaultChecking;
  }
};

const getDreamE2E = (dream) => dream?.e2e || { iv: "", ct: "", aad: "" };

const hasRealE2EPack = (dream) => {
  const pack = getDreamE2E(dream);
  return Boolean(pack.iv && pack.ct);
};

const normalizeDreamForPokoik = (dream, decrypted = null, copy) => {
  if (!dream) return null;
  const bodyHtml = decrypted?.bodyHtml ? sanitizeDreamHtml(decrypted.bodyHtml) : "";
  const text = extractDreamText(bodyHtml);
  const excerpt = text.length > 220 ? `${text.slice(0, 220).trim()}…` : text;
  const emotions = toArray(dream.emotions || dream?.meta?.emotions || dream.sen_emocje);
  const isDecrypted = Boolean(decrypted?.bodyHtml);

  return {
    id: dream.id || dream.wpId,
    title: dream.title || dream.sen_tytul || copy.untitledDream,
    dateHuman: formatDreamDate(dream, copy),
    dateTime: dream.date || dream?.meta?.date || dream.createdAt || "",
    icon: getDreamIcon(dream),
    excerpt: excerpt || copy.savedInCodex,
    excerptHtml: bodyHtml || "",
    encryptedExcerpt: copy.e2eLockedExcerpt,
    isE2E: true,
    isDecrypted,
    emotions,
    symbols: dream.symbols || dream?.meta?.symbols || dream.sen_symbole || "",
    e2e: getDreamE2E(dream),
  };
};

const buildPokoikData = (savedDreams, decryptedDreams = {}, copy) => {
  const realE2EDreams = savedDreams.filter(hasRealE2EPack);

  if (!realE2EDreams.length) return { stats: emptyPokoikStats, lastDream: null };

  const now = new Date();
  const sortedDreams = [...realE2EDreams].sort((a, b) => getDreamSortTime(b) - getDreamSortTime(a));
  const stats = sortedDreams.reduce((acc, dream) => {
    const date = toDate(dream);
    acc.all += 1;
    if (date && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()) acc.month += 1;
    if (isSameWeek(date, now)) acc.week += 1;
    return acc;
  }, { ...emptyPokoikStats });

  const lastRaw = sortedDreams[0];
  const lastId = lastRaw?.id || lastRaw?.wpId;

  return {
    stats,
    lastDream: normalizeDreamForPokoik(lastRaw, lastId ? decryptedDreams[lastId] : null, copy),
  };
};

const getProfileCopy = (profileStatus, copy) => {
  if (profileStatus.buildOptin && profileStatus.aiUseOptin) {
    return {
      status: profileStatus.hasPack ? copy.profile.active : copy.profile.enabledWaiting,
      text: copy.profile.enabledText,
      button: copy.profile.button,
    };
  }

  if (profileStatus.buildOptin) {
    return {
      status: copy.profile.buildingUnused,
      text: copy.profile.buildingText,
      button: copy.profile.button,
    };
  }

  return {
    status: copy.profile.inactive,
    text: copy.profile.inactiveText,
    button: copy.profile.button,
  };
};

const PokoikGate = ({ gate, copy }) => (
  <section className="mdPage" data-ui="gate">
    <div className="mdPage__inner">
      <div>
        <p className="mdPage__eyebrow">{copy.gateEyebrow}</p>
        <h1 className="mdPage__title">{copy.gateTitlePre} <em>Morfeusz Dreams</em></h1>
        <p className="mdPage__subtitle">{copy.gateSubtitle}</p>
      </div>
      <div className="mdCard">
        {gate.message && <div className="mdAlert mdAlert--info" data-ui="gate-message">{gate.message}</div>}
        <div className="mdRow">
          <a className="mdBtn mdBtn--primary mdBtn--row" data-md-login="1" href={gate.links.login}>{copy.signIn}</a>
          <a className="mdBtn mdBtn--row" href={gate.links.register}>{copy.register}</a>
          <a className="mdBtn mdBtn--row" href={gate.links.home}>{copy.home}</a>
        </div>
      </div>
    </div>
  </section>
);

const PokoikLoading = ({ copy }) => (
  <section className="mdP-hero" data-ui="mdP-loading">
    <div className="mdP-container">
      <header className="mdP-header"><h1 className="mdP-title">{copy.loadingTitle}</h1></header>
      <p className="mdP-sub">{copy.loadingSub}</p>
      <article className="mdP-card mdP-last md-fade" data-ui="mdP-loading-card">
        <div className="mdP-card-head">
          <span className="mdP-ico" aria-hidden="true">🌙</span>
          <div className="mdP-head-text"><h2 className="mdP-card-title">{copy.fetchingDreams}</h2><div className="mdP-card-date">{copy.loadingPatience}</div></div>
        </div>
        <p className="mdP-card-excerpt">{copy.openingPrivateSpace}</p>
      </article>
    </div>
  </section>
);

export const PokoikPage = () => {
  const { lang } = useLang();
  const { user, isAuthenticated, authReady } = useAuth();
  const copy = useMemo(() => getPokoikCopy(lang), [lang]);
  const [savedDreams, setSavedDreams] = useState([]);
  const [decryptedDreams, setDecryptedDreams] = useState({});
  const [isDreamsLoading, setIsDreamsLoading] = useState(true);
  const [dreamsError, setDreamsError] = useState("");
  const [profileStatus, setProfileStatus] = useState(() => getProfileContinuityStatus());
  const [vaultStatusLabel, setVaultStatusLabel] = useState(() => getVaultStatusLabel(copy));

  useEffect(() => {
    let mounted = true;

    const loadDreams = async () => {
      if (!authReady) return;
      if (!isAuthenticated) {
        setSavedDreams([]);
        setDecryptedDreams({});
        setDreamsError("");
        setIsDreamsLoading(false);
        return;
      }

      setIsDreamsLoading(true);
      setDreamsError("");
      try {
        const response = await dreamsClient.list();
        if (!mounted) return;
        setSavedDreams(Array.isArray(response.dreams) ? response.dreams : []);
      } catch (error) {
        if (!mounted) return;
        setSavedDreams([]);
        setDreamsError(error?.message || copy.fetchError);
      } finally {
        if (mounted) setIsDreamsLoading(false);
      }
    };

    loadDreams();
    const onFocus = () => loadDreams();
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [authReady, isAuthenticated, copy.fetchError]);

  useEffect(() => {
    let cancelled = false;

    const decryptUnlockedDreams = async () => {
      if (!isAuthenticated || !isVaultUnlocked()) return;
      const e2eDreams = savedDreams.filter(hasRealE2EPack);
      if (!e2eDreams.length) return;

      const next = {};
      for (const dream of e2eDreams) {
        const id = dream.id || dream.wpId;
        if (!id || decryptedDreams[id]?.bodyHtml) continue;
        try {
          const pack = getDreamE2E(dream);
          const plain = await window.morfeuszE2E.decryptText(pack.iv, pack.ct, pack.aad || "");
          next[id] = { bodyHtml: sanitizeDreamHtml(plain) };
        } catch (_) {
          next[id] = { error: true };
        }
      }

      if (!cancelled && Object.keys(next).length) setDecryptedDreams((current) => ({ ...current, ...next }));
    };

    decryptUnlockedDreams();
    window.addEventListener("morfeusz:vault:session", decryptUnlockedDreams);
    return () => {
      cancelled = true;
      window.removeEventListener("morfeusz:vault:session", decryptUnlockedDreams);
    };
  }, [savedDreams, decryptedDreams, isAuthenticated]);

  useEffect(() => {
    const syncVaultStatus = () => setVaultStatusLabel(getVaultStatusLabel(copy));
    syncVaultStatus();
    window.addEventListener("morfeusz:vault:session", syncVaultStatus);
    window.addEventListener("focus", syncVaultStatus);
    window.addEventListener("hashchange", syncVaultStatus);
    return () => {
      window.removeEventListener("morfeusz:vault:session", syncVaultStatus);
      window.removeEventListener("focus", syncVaultStatus);
      window.removeEventListener("hashchange", syncVaultStatus);
    };
  }, [copy]);

  useEffect(() => {
    let mounted = true;

    const syncLocalProfile = () => {
      if (mounted) setProfileStatus(getProfileContinuityStatus());
    };

    const syncRemoteProfile = async () => {
      if (!authReady || !isAuthenticated) {
        syncLocalProfile();
        return;
      }

      syncLocalProfile();
      try {
        const next = await syncProfileContinuityFromRest();
        if (mounted) setProfileStatus(next);
      } catch (_) {
        syncLocalProfile();
      }
    };

    syncRemoteProfile();
    window.addEventListener(PROFILE_CONTINUITY_CHANGE_EVENT, syncLocalProfile);
    window.addEventListener("storage", syncRemoteProfile);
    window.addEventListener("focus", syncRemoteProfile);
    return () => {
      mounted = false;
      window.removeEventListener(PROFILE_CONTINUITY_CHANGE_EVENT, syncLocalProfile);
      window.removeEventListener("storage", syncRemoteProfile);
      window.removeEventListener("focus", syncRemoteProfile);
    };
  }, [authReady, isAuthenticated]);

  const realE2EDreams = useMemo(() => savedDreams.filter(hasRealE2EPack), [savedDreams]);
  const pokoikData = useMemo(() => buildPokoikData(realE2EDreams, decryptedDreams, copy), [realE2EDreams, decryptedDreams, copy]);

  if (!authReady) return <PokoikLoading copy={copy} />;

  if (!isAuthenticated) {
    return <PokoikGate copy={copy} gate={{ message: "", links: { login: "#/zaloguj?redirect_to=/pokoik", register: "#/rejestracja?redirect_to=/pokoik", home: "#top" } }} />;
  }

  const { stats, lastDream } = pokoikData;
  const hasLastDream = Boolean(lastDream) && realE2EDreams.length > 0;
  const excerpt = hasLastDream && !lastDream?.isDecrypted ? lastDream.encryptedExcerpt : lastDream?.excerpt;
  const excerptHtml = hasLastDream && lastDream?.isDecrypted ? lastDream.excerptHtml : "";
  const editLastHref = hasLastDream ? `#/sen/${lastDream.id}?edit=1` : pokoikLinks.editLast;
  const lastDreamEmotions = toArray(lastDream?.emotions);
  const profileCopy = getProfileCopy(profileStatus, copy);

  const openProfileAction = () => {
    window.location.hash = "#/strefa";
  };

  return (
    <section className="mdP-hero" data-ui="mdP-root" data-build={POKOIK_REST_BUILD}>
      <div className="mdP-container">
        <header className="mdP-header">
          <h1 className="mdP-title">{copy.welcome} <span className="md-nick-under">{user?.displayName || user?.login || copy.dreamer}</span></h1>
        </header>

        <p className="mdP-sub">{copy.subtitle}</p>

        {dreamsError && <div className="md-alert md-info" data-ui="mdP-dreams-error">{dreamsError}</div>}

        <div className="mdP-grid">
          <article className="mdP-card mdP-last md-fade" data-ui="mdP-last-dream" data-e2e={hasLastDream ? "1" : "0"} data-e2e-iv={lastDream?.e2e.iv || ""} data-e2e-ct={lastDream?.e2e.ct || ""} data-e2e-aad={lastDream?.e2e.aad || ""}>
            <div className="mdP-card-head">
              <span className="mdP-ico" aria-hidden="true">{hasLastDream ? lastDream.icon : "💤"}</span>
              <div className="mdP-head-text">
                <h2 className="mdP-card-title">{isDreamsLoading ? copy.loadingTitle : hasLastDream ? lastDream.title : copy.noDreamTitle}</h2>
                {hasLastDream && <time className="mdP-card-date" dateTime={lastDream.dateTime}>{lastDream.dateHuman}</time>}
              </div>
            </div>

            {hasLastDream && lastDreamEmotions.length > 0 && (
              <div className="md-cta-row" data-ui="dream-emotions-row">
                {lastDreamEmotions.map((emotion) => <span className="md-chip" key={emotion} data-ui="dream-emotion-badge">{emotion}</span>)}
              </div>
            )}

            {hasLastDream ? (
              excerptHtml ? (
                <div className="mdP-card-excerpt" data-ui="mdP-last-excerpt" dangerouslySetInnerHTML={{ __html: excerptHtml }} />
              ) : (
                <p className="mdP-card-excerpt" data-ui="mdP-last-excerpt">{excerpt}</p>
              )
            ) : <div className="mdP-empty"><p>{isDreamsLoading ? copy.fetchingDreams : copy.noDreams}</p></div>}
          </article>

          <aside className="mdP-side">
            <div className="mdP-stat mdP-stat-big md-fade"><div className="mdP-stat-num">{stats.all}</div><div className="mdP-stat-label">{copy.allDreams}</div></div>
            <div className="mdP-stat-row">
              <div className="mdP-stat md-fade"><div className="mdP-stat-num">{stats.month}</div><div className="mdP-stat-label">{copy.thisMonth}</div></div>
              <div className="mdP-stat md-fade"><div className="mdP-stat-num">{stats.week}</div><div className="mdP-stat-label">{copy.thisWeek}</div></div>
            </div>
          </aside>
        </div>

        <div className="md-card mdP-vault" data-ui="mdP-vault"><div><strong>{copy.vaultE2E}</strong> <span id="md-vault-status">{vaultStatusLabel}</span></div></div>

        <div className="md-card mdP-vault" data-ui="mdP-profile-continuity">
          <div><strong>{copy.dreamTrace}</strong> <span data-ui="md-profile-status">{profileCopy.status}</span><div className="md-hint">{profileCopy.text}</div></div>
          <div className="md-cta-row" data-ui="mdP-profile-actions"><button type="button" className="md-btn md-secondary" onClick={openProfileAction}>{profileCopy.button}</button></div>
        </div>

        <div className="mdP-actions">
          <a className="md-btn md-primary" href={pokoikLinks.add}>{copy.addDream}</a>
          <a className="md-btn md-secondary" href={pokoikLinks.all}>{copy.allDreamsButton}</a>
          {hasLastDream && <a className="md-btn md-secondary" href={editLastHref}>{copy.editLast}</a>}
        </div>

        <div className="mdP-actions"><a className="md-btn md-secondary" href={pokoikLinks.home}>{copy.homeButton}</a></div>
      </div>
    </section>
  );
};
